
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Language, CustomRecipe, PantryItem } from '../types';
import { t } from '../locales';
import { searchFoods, searchSymptoms, searchItems, normalizeSymptom } from '../constants/autocompleteData';
import { parseInputLocally, ParsedItem } from '../services/textParsingService';

interface ManualEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'food' | 'symptom';
  onSave: (items: ParsedItem[]) => Promise<void>;
  language: Language;
  usedItemsHistory?: string[]; 
  customRecipes?: CustomRecipe[]; 
  pantryItems?: PantryItem[];
}

const ManualEntryModal: React.FC<ManualEntryModalProps> = ({ isOpen, onClose, type, onSave, language, usedItemsHistory = [], customRecipes = [], pantryItems = [] }) => {
  const [text, setText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  const [suggestions, setSuggestions] = useState<{name: string, isPantry: boolean, isRecipe: boolean}[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeWordInfo, setActiveWordInfo] = useState<{ word: string, startIndex: number, endIndex: number } | null>(null);

  const frequencyMap = useMemo(() => {
    const map: Record<string, number> = {};
    usedItemsHistory.forEach(item => {
        const key = item.toLowerCase();
        map[key] = (map[key] || 0) + 1;
    });
    return map;
  }, [usedItemsHistory]);

  const updateAutocomplete = (currentText: string, cursorPosition: number) => {
    const separators = /[,.\n]/;
    
    let start = cursorPosition;
    while (start > 0 && !separators.test(currentText[start - 1])) {
        start--;
    }

    const currentSegment = currentText.slice(start, cursorPosition).trim(); 
    
    if (currentSegment.length > 1) {
        if (type === 'food') {
            const dbMatches = searchFoods(currentSegment, language, 15);
            const recipeNames = customRecipes.map(r => r.name);
            const recipeMatches = searchItems(currentSegment, recipeNames, 5);
            const pantryNames = pantryItems.map(p => p.name);
            const pantryMatches = searchItems(currentSegment, pantryNames, 5);
            
            const results: {name: string, isPantry: boolean, isRecipe: boolean}[] = [];
            
            pantryMatches.forEach(name => results.push({ name, isPantry: true, isRecipe: false }));
            recipeMatches.forEach(name => {
                if (!results.some(r => r.name === name)) results.push({ name, isPantry: false, isRecipe: true });
            });
            dbMatches.forEach(name => {
                if (!results.some(r => r.name === name)) results.push({ name, isPantry: false, isRecipe: false });
            });

            results.sort((a, b) => {
                const countA = frequencyMap[a.name.toLowerCase()] || 0;
                const countB = frequencyMap[b.name.toLowerCase()] || 0;
                if (countB !== countA) return countB - countA;
                return 0; 
            });

            setSuggestions(results.slice(0, 10));
            setShowSuggestions(results.length > 0);
        } else {
            const symMatches = searchSymptoms(currentSegment, language, 10);
            setSuggestions(symMatches.map(name => ({ name, isPantry: false, isRecipe: false })));
            setShowSuggestions(symMatches.length > 0);
        }
        setActiveWordInfo({ word: currentSegment, startIndex: start, endIndex: cursorPosition });
    } else {
        setShowSuggestions(false);
        setActiveWordInfo(null);
    }
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const val = e.target.value;
      setText(val);
      updateAutocomplete(val, e.target.selectionStart);
  };

  const handleSelectSuggestion = (suggestion: string) => {
      if (!activeWordInfo) return;

      const before = text.slice(0, activeWordInfo.startIndex);
      const after = text.slice(textareaRef.current?.selectionStart || text.length);
      
      // Check if there's already a quantity before the word we are replacing
      const beforeTrimmed = before.trim();
      const hasQty = /\d+$/.test(beforeTrimmed);

      const newText = (before.endsWith(' ') || before === '' ? before : before + ' ') + suggestion + (after.startsWith(',') || after.startsWith('.') ? after : (after === '' ? '' : ' ' + after));
      setText(newText);
      setShowSuggestions(false);
      
      setTimeout(() => {
          if (textareaRef.current) {
              const newCursorPos = before.length + suggestion.length + 1;
              textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
          }
      }, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;

    setIsProcessing(true);
    
    // No AI here! Use the deterministic local parser.
    const parsedItems = parseInputLocally(
        text, 
        type === 'food' ? 'meal' : 'symptom'
    );
    
    await onSave(parsedItems);
    setIsProcessing(false);
    onClose();
    setText('');
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose}></div>
      <div className="bg-white dark:bg-gray-800 w-full max-w-lg rounded-2xl shadow-2xl p-6 relative z-10 animate-popIn border border-gray-100 dark:border-gray-700">
        <div className="flex justify-between items-center mb-2">
            <h3 className="text-lg font-bold text-gray-800 dark:text-white">
                {type === 'food' ? t(language, 'recordMeal') : t(language, 'recordSymptom')}
            </h3>
            <div className="flex items-center gap-1.5 bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded-full border border-blue-100 dark:border-blue-800">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                <span className="text-[9px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider">Fast Track Mode</span>
            </div>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
            {type === 'food' 
             ? "Direct entry. Format: '2 apples, 200g rice'. Names from suggestions are preserved exactly."
             : "Describe how you feel. E.g., 'Headache 6, bloating'."}
        </p>
        
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 relative">
            <div className="relative">
                <textarea
                    ref={textareaRef}
                    value={text}
                    onChange={handleTextChange}
                    rows={6}
                    className="w-full p-4 rounded-xl border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-emerald-500 outline-none resize-none text-base leading-relaxed"
                    placeholder={type === 'food' ? "e.g. 1 Apple, 2 slices of bread..." : "e.g. Headache 6, nausea..."}
                    autoFocus
                />
                
                {showSuggestions && suggestions.length > 0 && (
                    <div className="absolute top-full left-0 mt-2 bg-white dark:bg-gray-700 rounded-xl shadow-xl border border-gray-100 dark:border-gray-600 z-20 overflow-hidden w-full max-h-40 overflow-y-auto">
                        <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-100 dark:border-gray-600 text-[10px] font-bold text-gray-400 uppercase tracking-wider flex justify-between">
                            <span>Suggestions for "{activeWordInfo?.word}"</span>
                            <span className="text-emerald-500 dark:text-emerald-400">Library Search</span>
                        </div>
                        {suggestions.map((suggestion, idx) => {
                            const frequency = frequencyMap[suggestion.name.toLowerCase()] || 0;
                            return (
                                <button
                                    key={idx}
                                    type="button"
                                    onMouseDown={(e) => {
                                        e.preventDefault(); 
                                        handleSelectSuggestion(suggestion.name);
                                    }}
                                    className={`w-full text-left px-4 py-3 text-sm transition-colors border-b border-gray-50 dark:border-gray-600/50 last:border-0 flex justify-between items-center ${suggestion.isPantry ? 'bg-orange-50/40 dark:bg-orange-900/20 hover:bg-orange-100 border-l-4 border-l-orange-500' : 'text-gray-700 dark:text-gray-200 hover:bg-emerald-50 dark:hover:bg-emerald-900/30'}`}
                                >
                                    <div className="flex items-center gap-2">
                                        <span className={`font-bold ${suggestion.isPantry ? 'text-orange-700 dark:text-orange-400' : suggestion.isRecipe ? 'text-[#3F88C5]' : ''}`}>{suggestion.name}</span>
                                        {suggestion.isPantry && <span className="text-[9px] bg-orange-600 text-white px-1.5 py-0.5 rounded font-black shadow-sm">PANTRY</span>}
                                        {suggestion.isRecipe && <span className="text-[9px] bg-[#3F88C5] text-white px-1.5 py-0.5 rounded font-bold uppercase shadow-sm">RECIPE</span>}
                                    </div>
                                    {frequency > 0 && (
                                        <span className="text-[10px] bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-300 px-1.5 py-0.5 rounded-full">
                                            {frequency}x
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className="flex gap-3 mt-2">
                <button 
                    type="button"
                    onClick={onClose}
                    className="flex-1 py-3 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 font-bold hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                    {t(language, 'cancel')}
                </button>
                <button 
                    type="submit"
                    disabled={isProcessing || !text.trim()}
                    className="flex-1 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-lg transition-transform active:scale-95 disabled:opacity-50 disabled:scale-100 flex items-center justify-center gap-2"
                >
                    {isProcessing ? (
                        <>
                           <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                           {t(language, 'processing')}
                        </>
                    ) : (
                        t(language, 'save')
                    )}
                </button>
            </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default ManualEntryModal;
